import { useEffect, useId, useRef } from 'react'
import { Button, Eyebrow, Lang, Magnetic, PrismLight, Segments } from '@/components/ui'
import { cta } from '@/content/site'
import { useInView } from '@/hooks/useInView'
import { MEDIA, useMediaQuery } from '@/hooks/useMediaQuery'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { clamp, damp, smoothstep } from '@/lib/math'
import { pointer } from '@/lib/pointer'
import { drift, onTick, useMaskedReveal } from '@/sections/Call/motion'
import styles from './Cta.module.css'

/** Concentric rings breathing out of the button, each drawn twice for a prism fringe. */
const RINGS = 7
/** Seconds for one ring to travel from the button to the edge of the stage. */
const PERIOD = 11
/** The hover ripple: seconds, and how far it reaches (fraction of the outer radius). */
const BURST_SECONDS = 2.8
const BURST_REACH = 0.72

interface RingNodes {
  iris: SVGCircleElement
  prism: SVGCircleElement
}

/**
 * "08 — Start". A pearl stage under an iridescent wash: the romanised-Hindi kicker set huge,
 * "Vokarta ko *call laga.*", the translation, the lead and one big magnetic Telegram button.
 * Slow prism-tinted ripple rings breathe out of the button behind frosted glass (light
 * through glass), lean a little toward the pointer, and the button's hover sends one
 * stronger ripple. Small text stays slate for contrast. Theme: `prism`.
 */
export function Cta() {
  const rootRef = useRef<HTMLElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const actionRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const sheenRef = useRef<HTMLSpanElement>(null)
  const burstAt = useRef(-100)
  const clock = useRef(0)
  // url(#…) references must survive any characters React puts in its ids.
  const gradientId = `cta-prism-${useId().replace(/[^\w-]/g, '')}`
  const reduced = useReducedMotion()
  const finePointer = useMediaQuery(MEDIA.hover)
  const inView = useInView(rootRef, { rootMargin: '10% 0px' })
  useMaskedReveal(rootRef)

  useEffect(() => {
    const root = rootRef.current
    const svg = svgRef.current
    const action = actionRef.current
    const stage = stageRef.current
    if (!root || !svg || !action || !stage) return
    if (!reduced && !inView) return

    const rings: RingNodes[] = Array.from(svg.querySelectorAll<SVGGElement>('[data-ring]')).map(
      (group) => {
        const [iris, prism] = Array.from(group.querySelectorAll('circle'))
        return { iris, prism }
      },
    )
    const burst = svg.querySelector<SVGGElement>('[data-burst]')
    const burstIris = burst?.querySelector<SVGCircleElement>('[data-iris]')
    const burstPrism = burst?.querySelector<SVGCircleElement>('[data-prism]')

    // Geometry in section pixels: the rings' origin is the button's resting centre.
    let width = 1
    let height = 1
    let originX = 0
    let originY = 0
    let inner = 120
    let outer = 600
    let stageWidth = 1
    let stageHeight = 1
    const measure = () => {
      const box = root.getBoundingClientRect()
      const anchor = action.getBoundingClientRect()
      width = Math.max(1, box.width)
      height = Math.max(1, box.height)
      stageWidth = stage.offsetWidth
      stageHeight = stage.offsetHeight
      svg.setAttribute('viewBox', `0 0 ${width.toFixed(0)} ${height.toFixed(0)}`)
      originX = anchor.left + anchor.width / 2 - box.left
      originY = anchor.top + anchor.height / 2 - box.top
      inner = Math.max(anchor.width, anchor.height) * 0.5 + 8
      // Fade out well before the corners: the section's own colour stays pure there.
      outer = Math.max(inner + 1, Math.min(Math.hypot(width, height) * 0.42, width * 0.62))
    }

    const place = (ring: RingNodes, radius: number, alpha: number, cx: number, cy: number) => {
      ring.iris.setAttribute('cx', cx.toFixed(1))
      ring.iris.setAttribute('cy', cy.toFixed(1))
      ring.iris.setAttribute('r', radius.toFixed(1))
      ring.iris.style.opacity = (alpha * 0.5).toFixed(3)
      // The prism copy sits a hair outside: a dispersion fringe, as through glass.
      ring.prism.setAttribute('cx', (cx + 1.5).toFixed(1))
      ring.prism.setAttribute('cy', (cy + 1).toFixed(1))
      ring.prism.setAttribute('r', (radius + 3).toFixed(1))
      ring.prism.style.opacity = alpha.toFixed(3)
    }

    // Reduced motion: one still composition, redrawn only on resize.
    if (reduced) {
      const drawStill = () => {
        measure()
        rings.forEach((ring, index) => {
          const u = (index + 0.6) / RINGS
          place(ring, inner + u * (outer - inner), (1 - u) ** 1.4 * 0.85, originX, originY)
        })
      }
      drawStill()
      const observer = new ResizeObserver(drawStill)
      observer.observe(root)
      return () => observer.disconnect()
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(root)
    observer.observe(action)
    observer.observe(stage)

    let leanX = 0
    let leanY = 0
    let lightX = Number.NaN
    let lightY = Number.NaN

    const remove = onTick((dt, time) => {
      const box = finePointer ? root.getBoundingClientRect() : null
      const stageBox = finePointer ? stage.getBoundingClientRect() : null
      clock.current += dt
      const now = clock.current

      // Lean very slightly toward the pointer while it's over the section.
      let aimX = 0
      let aimY = 0
      if (box && pointer.lastMove > 0 && pointer.y >= box.top && pointer.y <= box.bottom) {
        aimX = clamp((pointer.x - box.left - originX) / (width * 0.5), -1, 1)
        aimY = clamp((pointer.y - box.top - originY) / (height * 0.5), -1, 1)
      }
      leanX = damp(leanX, aimX, 1.8, dt)
      leanY = damp(leanY, aimY, 1.8, dt)

      const breathe = 1 + 0.018 * Math.sin(time * 0.55)
      for (let k = 0; k < rings.length; k++) {
        const u = (now / PERIOD + k / rings.length) % 1
        const radius = (inner + u * (outer - inner)) * breathe
        const alpha = smoothstep(0, 0.14, u) * (1 - u) ** 1.7
        const reach = (radius - inner) / (outer - inner)
        const cx = originX + leanX * reach * 34
        const cy = originY + leanY * reach * 24
        place(rings[k], radius, alpha, cx, cy)
      }

      // The hover ripple: quick to leave, slow to fade.
      if (burstIris && burstPrism) {
        const age = (now - burstAt.current) / BURST_SECONDS
        if (age >= 0 && age <= 1) {
          const eased = 1 - (1 - age) ** 3
          const radius = inner + eased * (outer - inner) * BURST_REACH
          const alpha = (1 - age) ** 2
          place({ iris: burstIris, prism: burstPrism }, radius, alpha * 1.6, originX, originY)
        } else if (burstIris.style.opacity !== '0') {
          burstIris.style.opacity = '0'
          burstPrism.style.opacity = '0'
        }
      }

      // Light through glass: a soft highlight on the stage follows the cursor, or drifts.
      const sheen = sheenRef.current
      if (sheen) {
        const w = stageWidth
        const h = stageHeight
        let x = (0.5 + 0.34 * drift(time * 0.14, 2)) * w
        let y = (0.3 + 0.22 * drift(time * 0.12, 6)) * h
        if (stageBox && pointer.lastMove > 0) {
          x = clamp(pointer.x - stageBox.left, -0.1 * w, 1.1 * w)
          y = clamp(pointer.y - stageBox.top, -0.1 * h, 1.1 * h)
        }
        if (Number.isNaN(lightX)) {
          lightX = x
          lightY = y
        }
        lightX = damp(lightX, x, 2.6, dt)
        lightY = damp(lightY, y, 2.6, dt)
        sheen.style.transform = `translate3d(${lightX.toFixed(1)}px, ${lightY.toFixed(1)}px, 0)`
      }
    })

    return () => {
      remove()
      observer.disconnect()
    }
  }, [reduced, inView, finePointer])

  const sendRipple = () => {
    if (reduced) return
    if (clock.current - burstAt.current > BURST_SECONDS * 0.45) burstAt.current = clock.current
  }

  return (
    <section
      ref={rootRef}
      id="start"
      aria-labelledby="start-title"
      className={styles.root}
      data-theme="prism"
    >
      <PrismLight intensity={0.75} />
      <span className={styles.caustic} aria-hidden="true" />
      <svg ref={svgRef} className={styles.rings} aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" className={styles.stopLilac} />
            <stop offset="0.35" className={styles.stopSky} />
            <stop offset="0.65" className={styles.stopMint} />
            <stop offset="1" className={styles.stopBlush} />
          </linearGradient>
        </defs>
        {Array.from({ length: RINGS }, (_, index) => (
          <g key={index} data-ring>
            <circle className={styles.ringIris} r="0" />
            <circle className={styles.ringPrism} r="0" stroke={`url(#${gradientId})`} />
          </g>
        ))}
        <g data-burst>
          <circle className={styles.burstIris} r="0" data-iris />
          <circle className={styles.burstPrism} r="0" stroke={`url(#${gradientId})`} data-prism />
        </g>
      </svg>

      <div className={`container ${styles.inner}`}>
        <div ref={stageRef} className={styles.stage}>
          <span className={styles.sheen} aria-hidden="true">
            <span ref={sheenRef} className={styles.sheenSpot} />
          </span>
          <Eyebrow className={styles.eyebrow}>{cta.eyebrow}</Eyebrow>
          <p className={styles.kicker} data-reveal>
            <Lang code="hi-Latn">{cta.kicker}</Lang>
          </p>
          <h2 id="start-title" className={styles.title} lang="hi-Latn" data-reveal>
            <Segments segments={cta.title} />
          </h2>
          <p className={styles.translation} data-reveal>
            {cta.translation}
          </p>
          <p className={styles.lead} data-reveal>
            {cta.lead}
          </p>
          <div
            ref={actionRef}
            className={styles.action}
            onPointerEnter={sendRipple}
            onFocus={sendRipple}
          >
            <Magnetic strength={0.3}>
              <Button
                href={cta.button.href}
                external={cta.button.external}
                size="lg"
                icon="arrow-up-right"
                className={styles.button}
              >
                {cta.button.label}
              </Button>
            </Magnetic>
          </div>
          <p className={styles.note}>{cta.note}</p>
        </div>
      </div>
    </section>
  )
}
